/**
 * useDateDaySync - Hook for synchronizing date/dayIndex state
 *
 * Manages the bidirectional sync between:
 * - selectedDate (date picker)
 * - dayIndex (graph navigation)
 * - loadProfile.startDate (load year base)
 * - optimization results (for auto-selecting peak load day)
 *
 * @module hooks/useDateDaySync
 */

import { useState, useEffect } from 'react';
import { debug } from '../utils/debug';

/**
 * @typedef {Object} DateDaySyncOptions
 * @property {Object} loadProfile - Load profile state (with startDate)
 * @property {Object|null} optimizationResults - Optimization results (with outputs)
 */

/**
 * @typedef {Object} DateDaySyncReturn
 * @property {Date} selectedDate - Currently selected date
 * @property {Function} setSelectedDate - Setter for selected date
 * @property {string} dayIndex - Current day index (0-364)
 * @property {Function} setDayIndex - Setter for day index
 */

/**
 * Hook for managing date/day index synchronization
 *
 * Handles:
 * 1. Updating selectedDate when dayIndex changes
 * 2. Resetting selectedDate when load year changes
 * 3. Auto-selecting peak load day when optimization results load
 *
 * @param {DateDaySyncOptions} options
 * @returns {DateDaySyncReturn}
 */
export function useDateDaySync({ loadProfile, optimizationResults }) {
    const [selectedDate, setSelectedDate] = useState(() => new Date());
    const [dayIndex, setDayIndex] = useState("0");

    // Update selectedDate when dayIndex changes externally (e.g. from graph nav)
    useEffect(() => {
        const newDate = new Date(loadProfile.startDate);
        newDate.setDate(newDate.getDate() + parseInt(dayIndex, 10));
        if (newDate.getTime() !== selectedDate.getTime()) {
            setSelectedDate(newDate);
        }
    }, [dayIndex, loadProfile.startDate, selectedDate]);

    // Update selected date when load year changes
    useEffect(() => {
        setSelectedDate(loadProfile.startDate);
    }, [loadProfile.startDate]);

    // Find and select the peak load day when results are loaded
    useEffect(() => {
        if (optimizationResults?.outputs) {
            try {
                const out = optimizationResults.outputs;
                const loadObj = out.ElectricLoad || out.Scenario?.Site?.ElectricLoad;
                const loads = loadObj?.load_series_kw || [];

                if (loads.length > 0) {
                    // Find peak hour and convert to day index
                    let maxLoad = -1;
                    let peakHour = 0;
                    for (let i = 0; i < loads.length; i++) {
                        if (loads[i] > maxLoad) {
                            maxLoad = loads[i];
                            peakHour = i;
                        }
                    }
                    const peakDay = Math.floor(peakHour / 24);
                    debug.log(`[useDateDaySync] Peak load of ${maxLoad.toFixed(0)} kW found at hour ${peakHour} (day ${peakDay})`);

                    // Update day index to peak day
                    setDayIndex(String(peakDay));

                    // Update date picker to match
                    const newDate = new Date(loadProfile.startDate);
                    newDate.setDate(newDate.getDate() + peakDay);
                    setSelectedDate(newDate);
                }
            } catch (err) {
                debug.warn("[useDateDaySync] Could not determine peak load day", err);
            }
        }
    }, [optimizationResults, loadProfile.startDate]);

    return {
        selectedDate,
        setSelectedDate,
        dayIndex,
        setDayIndex,
    };
}

export default useDateDaySync;
